import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApplicationScreeningDecision } from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { GoodFitSurveyValidationSchema, goodFitSurveyValidationSchema } from './goodFitSurveyValidationSchema'
import GoodFitWizardForm from './GoodFitWizardForm'
import { useScreenForCreditApplication } from './ScreenForCreditApplication.gql'

const GoodFitSurveyPage = () => {
  const [makeTestId] = useNameFormatter('GoodFitSurveyPage')
  const navigate = useNavigate()
  const { email } = useAuth()
  const showToast = useToast()
  const { search } = useLocation()
  const { tracker } = useWebTracking()

  const methods = useForm<GoodFitSurveyValidationSchema>({
    defaultValues: {
      annualRevenue: '',
      currentCash: '',
      creditRange: '',
      __currentStep: 1,
      __totalStepCount: 3,
    },
    resolver: yupResolver(goodFitSurveyValidationSchema),
  })
  const { handleSubmit, reset } = methods

  const [screenForCreditApplication, { loading: isScreeningForCreditApplication }] = useScreenForCreditApplication({
    onCompleted: (data) => {
      const isCleared = data?.screenForCreditApplication?.decision === ApplicationScreeningDecision.CLEARED

      logInfo('screenForCreditApplication', {
        attributes: {
          isCleared,
          decision: data?.screenForCreditApplication?.decision,
          result: `Sending user to path ${
            isCleared ? PATHNAME_CONSTS.GOOD_FIT_RESULT_PATH : PATHNAME_CONSTS.NOT_GOOD_FIT_RESULT_PATH
          }`,
        },
        eventName: 'screenForCreditApplicationSuccess',
      })

      isCleared
        ? navigate({ pathname: PATHNAME_CONSTS.GOOD_FIT_RESULT_PATH, search }, { replace: true })
        : navigate(
            {
              pathname: PATHNAME_CONSTS.NOT_GOOD_FIT_RESULT_PATH,
              search,
            },
            {
              state: {
                dsaVersion: data?.screenForCreditApplication?.dsaVersion,
              },
              replace: true,
            },
          )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'screenForCreditApplication',
          message: error.message,
          result: 'Error',
        },
        eventName: 'screenForCreditApplicationError',
      })

      tracker?.track('se_signup__error_message__fired', {
        error_field: 'screenForCreditApplication',
        error_message_text: error.message,
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='An error occurred while screening for credit application.'
          id='sputnik.GoodFitSurveyPage__Vcjh4E'
        />,
        { className: 'fs-unmask' },
      )
      reset()
    },
  })

  const onSubmitGoodFitSurvey = (formValues) => {
    const { annualRevenue, currentCash, creditRange } = formValues

    screenForCreditApplication({
      variables: {
        screeningDecisionInput: {
          applicantEmail: (email as string) || '',
          annualRevenue,
          currentCash,
          creditRange,
        },
      },
    })
  }

  return (
    <>
      <BillBrandLogo />
      <FormProvider {...methods}>
        <form
          className='fs-unmask'
          data-testid={makeTestId('form')}
          id={makeTestId('form')}
          onSubmit={handleSubmit(onSubmitGoodFitSurvey)}
        >
          <GoodFitWizardForm isScreeningForCreditApplication={isScreeningForCreditApplication} />
        </form>
      </FormProvider>
    </>
  )
}

export default GoodFitSurveyPage
